import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Send, Sparkles, Loader2, CheckCircle2, ListTodo, Target, GraduationCap, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ParentDigest() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState("");
  const [gen, setGen] = useState(false);

  useEffect(() => { api.get("/digest").then((r) => setData(r.data)); }, []);

  const generate = async () => {
    if (!data) return;
    setGen(true);
    try {
      const r = await api.post("/digest/summary", { stats: data.stats });
      setSummary(r.data.summary);
    } catch {
      toast.error("Could not generate the summary. Try again.");
    } finally {
      setGen(false);
    }
  };

  const emailSoon = () => toast("Email delivery is coming soon — for now this digest lives here in-app.", { icon: "📧" });

  if (!data) return <div className="text-slate-400 text-sm">Loading digest…</div>;

  const s = data.stats;
  const weekOf = new Date(data.week_of).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  const stats = [
    { label: "Tasks done this week", value: s.tasks_completed_week, Icon: CheckCircle2, cls: "bg-emerald-500" },
    { label: "Tasks pending", value: s.tasks_pending, Icon: ListTodo, cls: "bg-rose-500" },
    { label: "Avg goal progress", value: `${s.avg_goal_progress}%`, Icon: Target, cls: "bg-purple-500" },
    { label: "Exams in 2 weeks", value: s.upcoming_exams_count, Icon: GraduationCap, cls: "bg-indigo-500" },
  ];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 to-purple-600 p-7 sm:p-8 text-white">
        <div className="absolute -right-10 -top-10 w-52 h-52 bg-white/10 rounded-full blur-2xl" />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">Week of {weekOf}</p>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold mt-1">Ayaan's Weekly Digest</h2>
        <p className="text-indigo-100 mt-1 text-sm flex items-center gap-1"><Mail className="w-4 h-4" /> For {data.parent_email}</p>
        <Button data-testid="email-digest-btn" onClick={emailSoon} className="mt-4 rounded-xl bg-white text-indigo-700 hover:bg-indigo-50">
          <Send className="w-4 h-4 mr-1" /> Email me this (coming soon)
        </Button>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((st, i) => {
          const Icon = st.Icon;
          return (
            <motion.div key={st.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className={`w-10 h-10 rounded-xl ${st.cls} flex items-center justify-center mb-3`}><Icon className="w-5 h-5 text-white" /></div>
              <p className="text-2xl font-heading font-bold text-slate-900">{st.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{st.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* AI summary */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-500" /> This week in a nutshell</h3>
          <Button data-testid="gen-summary-btn" onClick={generate} disabled={gen} size="sm" className="rounded-lg bg-indigo-600 hover:bg-indigo-700">
            {gen ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Writing…</> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> {summary ? "Rewrite" : "Generate summary"}</>}
          </Button>
        </div>
        {summary
          ? <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="digest-summary">{summary}</p>
          : <p className="text-sm text-slate-400">Tap "Generate summary" for a warm, AI-written recap of Ayaan's week.</p>}
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Panel title="Completed this week" empty="No tasks completed yet this week.">
          {data.completed_week.map((t, i) => (
            <Row key={i} left={t.title} right={t.subject} dot="bg-emerald-500" />
          ))}
        </Panel>
        <Panel title="Still pending" empty="Nothing pending — all clear!">
          {data.pending.map((t, i) => (
            <Row key={i} left={t.title} right={t.subject} dot={t.priority === "high" ? "bg-red-500" : t.priority === "medium" ? "bg-amber-500" : "bg-emerald-500"} />
          ))}
        </Panel>
        <Panel title="Goals moved this week" empty="No goal progress changed this week.">
          {data.goals_moved.map((g, i) => (
            <div key={i} className="flex items-center gap-2 py-2">
              <span className={`w-1.5 h-1.5 rounded-full ${g.delta > 0 ? "bg-emerald-500" : "bg-rose-500"}`} />
              <span className="text-sm text-slate-700 flex-1 truncate">{g.title}</span>
              <span className={`text-xs font-bold ${g.delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>{g.delta > 0 ? "+" : ""}{g.delta}%</span>
              <span className="text-xs text-slate-400">→ {g.progress}%</span>
            </div>
          ))}
        </Panel>
        <Panel title="All goals" empty="No goals set yet.">
          {data.goals.map((g, i) => (
            <div key={i} className="py-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-700 truncate">{g.title}</span>
                <span className="text-xs font-bold text-indigo-600">{g.progress}%</span>
              </div>
              <Progress value={g.progress} className="h-1.5" />
            </div>
          ))}
        </Panel>
        <Panel title="Exams in the next 2 weeks" empty="No exams in the next two weeks.">
          {data.upcoming_exams.map((e, i) => (
            <Row key={i} left={`${e.subject}${e.topic ? " · " + e.topic : ""}`} right={new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} dot="bg-indigo-500" />
          ))}
        </Panel>
      </div>
    </div>
  );
}

const Panel = ({ title, empty, children }) => {
  const has = Array.isArray(children) ? children.length > 0 : !!children;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <h4 className="font-heading font-semibold text-slate-900 mb-3">{title}</h4>
      {has ? <div className="divide-y divide-slate-50">{children}</div> : <p className="text-sm text-slate-400">{empty}</p>}
    </div>
  );
};

const Row = ({ left, right, dot }) => (
  <div className="flex items-center gap-2 py-2">
    <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
    <span className="text-sm text-slate-700 flex-1 truncate">{left}</span>
    <span className="text-xs text-slate-400">{right}</span>
  </div>
);
